import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthToken } from '../../../util/auth';
import { getApiChara } from '../../../util/getApi';
import { json } from 'react-router-dom';
import Loader from '../../UI/Loader';
import { FaCapsules, FaSearch, FaSortAmountDown, FaCalendarAlt, FaIdBadge, FaPlus, FaTag, FaInfoCircle, FaEye, FaEdit } from 'react-icons/fa';
import classes from './MedicineList.module.css';
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 10;

const MedicineList = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [medicineLists, setMedicineLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [filteredTableByName, setFilteredTableByName] = useState([]);
  const loaderRef = useRef(null);
  const navigate = useNavigate();
  const currentDate = new Date();
  const [sortOrder, setSortOrder] = useState('asc');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const token = getAuthToken();
      const apiChar = getApiChara();
      if (token === null || token === 'EXPIRED') {
        navigate('/');
      }
      const apiUrl = `${apiChar}/medicine/getAllMedicine?page=${currentPage}&size=${PAGE_SIZE}`;
      setIsLoading(true);
      try {
        const response = await fetch(apiUrl, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });

        if (!response.ok) {
          throw json({ message: 'could not fetch medicines' }, { status: 500 });
        }

        const data = await response.json();
        if (data.length < PAGE_SIZE) {
          setHasMore(false);
        }
        setMedicineLists((prevData) => [...prevData, ...data]);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching medicines:', error);
        setLoading(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  const onSearchMedicineHandler = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (token === null || token === 'EXPIRED') {
      navigate('/');
    }
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/medicine/getMedicineByName?name=${searchName}`;

    setIsLoading(true);
    try {
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (!response.ok) {
        throw new Error('Could Not fetch details');
      }

      const data = await response.json();
      setMedicineLists(data);
      setFilteredTableByName(data);
    } catch (error) {
      console.error('Error fetching medicine data: ', error);
    } finally {
      setIsLoading(false);
    }
  };


  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting && hasMore && !loading) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '20px',
      threshold: 0.1,
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loaderRef, hasMore, loading]);

  useEffect(() => {
    if (!searchName) {
      setFilteredTableByName(medicineLists);
    }
  }, [medicineLists, searchName]);

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const sortedTable = useMemo(() => {
    return [...filteredTableByName].sort((a, b) => {
      return sortOrder === 'asc' ? a.medicineId - b.medicineId : b.medicineId - a.medicineId;
    });
  }, [filteredTableByName, sortOrder]);

  const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;

  const newMedicineHandler = () => {
    navigate('new');
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="container">
        <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
          <h4 style={{ color: 'var(--primary-color)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            {t("Medicine Information")}
          </h4>
          <span className="badge bg-light text-dark border p-2" style={{ fontSize: '0.85rem' }}>
            <FaCalendarAlt className="me-1" style={{ color: 'var(--primary-color)' }} /> {formattedDate}
          </span>
        </div>

        <div className="row g-3 align-items-center mb-4">
          <div className="col-md-6 col-sm-12">
            <form onSubmit={onSearchMedicineHandler}>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <FaSearch className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder={t("Search Medicine by Name...")}
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  required
                  style={{ boxShadow: 'none' }}
                />
                <button
                  className={`${classes.premiumBtn} border-0`}
                  type="submit"
                  style={{ borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px' }}
                >
                  <FaSearch className="me-2" /> {t("Search")}
                </button>
              </div>
            </form>
          </div>
          <div className="col-md-6 col-sm-12 d-flex justify-content-md-end gap-2">
            <button
              className={classes.premiumBtn}
              onClick={newMedicineHandler}
            >
              <FaPlus className="me-2" /> {t("New Medicine")}
            </button>
          </div>
        </div>
      </div>

      <div className={classes.tableContainer}>
        <Table responsive hover className={classes.customTable}>
          <thead>
            <tr>
              <th onClick={toggleSortOrder} style={{ cursor: 'pointer', width: '120px' }}>
                {t("Medicine-Id")} <FaSortAmountDown className="ms-2 opacity-50" />
              </th>
              <th className="text-start ps-4">
                <FaCapsules className="me-2" /> {t("Name")}
              </th>
              <th>{t("Type")}</th>
              <th>{t("Manufacturer")}</th>
              <th>{t("Price")}</th>
              <th>{t("Barcode")}</th>
              <th>{t("Expiry")}</th>
              <th style={{ width: '120px' }}>{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedTable.length === 0 && (
              <tr>
                <td colSpan="8" className="py-5 text-center text-muted">
                  <FaInfoCircle className="fa-2x mb-2 d-block opacity-50 mx-auto" />
                  {t("No medicine records found.")}
                </td>
              </tr>
            )}
            {sortedTable.map((med, index) => (
              <tr key={`${index}`}>
                <td>
                  <span className="badge rounded-pill bg-light text-dark border">{med?.medicineId}</span>
                </td>
                <td className="text-start ps-4 fw-500">{med?.name}</td>
                <td>{med?.medType}</td>
                <td>{med?.manufacturer || "-"}</td>
                <td>{med?.price || "-"}</td>
                <td>{med?.barcode || "-"}</td>
                <td>{med?.expiryDate || "-"}</td>
                <td>
                  <Link to={`${med?.medicineId}`} className="btn-outline-premium btn btn-sm px-3" title={t("Detail")}>
                    <FaEye />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-flex justify-content-center mt-4" ref={loaderRef}>
        {loading && <div>{t("Loading...")}</div>}
      </div>
    </>
  );
};

export default MedicineList;
